import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Trash, X, Backpack, Check, Sparkle, Sun } from '@phosphor-icons/react';
import { PACKING_CATEGORIES } from './TripCard';

export default function PackingTab({ api, trip }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showAi, setShowAi] = useState(false);
  const [seedBusy, setSeedBusy] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/trips/${trip.trip_id}/packing`);
      setItems(r.data || []);
    } catch (_) { /* ignore */ }
    setLoading(false);
  }, [api, trip.trip_id]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const togglePacked = async (item) => {
    setItems(prev => prev.map(x => x.item_id === item.item_id ? { ...x, packed: !x.packed } : x));
    try {
      await api.patch(`/trips/${trip.trip_id}/packing/${item.item_id}`, { packed: !item.packed });
    } catch (_) {
      setItems(prev => prev.map(x => x.item_id === item.item_id ? { ...x, packed: item.packed } : x));
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/trips/${trip.trip_id}/packing/${id}`);
      setItems(prev => prev.filter(x => x.item_id !== id));
    } catch (_) { /* ignore */ }
  };

  const handleSeed = async () => {
    setSeedBusy(true);
    try {
      await api.post(`/trips/${trip.trip_id}/packing/seed-defaults`);
      await fetchItems();
    } catch (_) { /* ignore */ }
    setSeedBusy(false);
  };

  const packed = items.filter(x => x.packed).length;
  const total = items.length;
  const pct = total > 0 ? Math.round((packed / total) * 100) : 0;

  const byCat = {};
  items.forEach(it => {
    const k = it.category || 'general';
    if (!byCat[k]) byCat[k] = [];
    byCat[k].push(it);
  });

  return (
    <div className="space-y-4" data-testid="packing-tab">
      {total > 0 && (
        <div className="rounded-2xl p-4"
          style={{ background: 'linear-gradient(135deg, rgba(34,197,94,0.10), rgba(34,197,94,0.02))', border: '1px solid rgba(34,197,94,0.25)' }}
          data-testid="packing-progress">
          <div className="flex items-center justify-between gap-2 mb-2">
            <p className="text-sm font-bold text-emerald-300">
              {packed} / {total} packed
            </p>
            <p className="text-2xl font-extrabold" style={{ color: pct === 100 ? '#22C55E' : '#86EFAC' }}>{pct}%</p>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
            <div className="h-full transition-all duration-500"
              style={{ width: `${pct}%`, background: pct === 100 ? '#22C55E' : 'linear-gradient(90deg,#10B981,#86EFAC)' }} />
          </div>
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-neutral-400">{total === 0 ? 'Empty list' : `${total} item(s)`}</p>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setShowAi(true)} data-testid="packing-ai-btn"
            className="px-3 py-1.5 rounded-lg text-[11px] font-bold flex items-center gap-1.5"
            style={{ background: 'linear-gradient(135deg, rgba(168,85,247,0.20), rgba(236,72,153,0.20))', color: '#E9D5FF', border: '1px solid rgba(168,85,247,0.40)' }}>
            <Sparkle size={11} weight="fill" /> AI Pack for me
          </button>
          {total === 0 && (
            <button onClick={handleSeed} disabled={seedBusy} data-testid="packing-seed-btn"
              className="px-3 py-1.5 rounded-lg bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 text-[11px] font-bold hover:bg-emerald-500/25 disabled:opacity-50">
              {seedBusy ? 'Adding…' : '16 essentials'}
            </button>
          )}
          <button onClick={() => setShowForm(true)} data-testid="packing-add-btn"
            className="px-3 py-1.5 rounded-lg bg-[#FF6B35] text-white text-[11px] font-bold hover:bg-[#E85D2A] flex items-center gap-1.5">
            <Plus size={11} weight="bold" /> Add item
          </button>
        </div>
      </div>

      {loading && <div className="text-center py-6 text-sm text-neutral-500">Loading…</div>}
      {!loading && total === 0 && (
        <div className="rounded-xl p-8 text-center" style={{ background: 'rgba(34,197,94,0.05)', border: '1px dashed rgba(34,197,94,0.30)' }}
          data-testid="packing-empty">
          <Backpack size={28} weight="duotone" className="text-emerald-400 mx-auto mb-2" />
          <p className="text-sm text-neutral-300 mb-1">No packing list yet.</p>
          <p className="text-[11px] text-neutral-500">Tap <b>AI Pack</b> for a destination-aware list, or <b>16 essentials</b> for basics.</p>
        </div>
      )}

      {!loading && total > 0 && (
        <div className="space-y-3">
          {PACKING_CATEGORIES.filter(c => byCat[c.id]).map(c => (
            <div key={c.id} className="rounded-2xl overflow-hidden" data-testid={`packing-cat-${c.id}`}
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="px-4 py-2 flex items-center gap-2" style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <span className="text-base">{c.emoji}</span>
                <span className="text-xs font-bold uppercase tracking-wider text-neutral-300">{c.label}</span>
                <span className="text-[10px] text-neutral-500 ml-auto">
                  {byCat[c.id].filter(x => x.packed).length} / {byCat[c.id].length}
                </span>
              </div>
              <div className="divide-y divide-white/5">
                {byCat[c.id].map(it => (
                  <div key={it.item_id} className="px-4 py-2.5 flex items-center gap-3 group hover:bg-white/[0.02]"
                    data-testid={`packing-item-${it.item_id}`}>
                    <button onClick={() => togglePacked(it)}
                      data-testid={`packing-toggle-${it.item_id}`}
                      className={`w-5 h-5 rounded-md flex items-center justify-center transition-all flex-shrink-0 ${it.packed ? 'bg-emerald-500 border-emerald-500' : 'bg-transparent border border-white/20 hover:border-emerald-400'}`}>
                      {it.packed && <Check size={11} weight="bold" className="text-white" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${it.packed ? 'text-neutral-500 line-through' : 'text-white'}`}>
                        {it.label}
                        {it.qty > 1 && <span className="text-[10px] text-neutral-500 ml-1">×{it.qty}</span>}
                        {it.ai_generated && <span className="ml-1.5 inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider text-purple-300"><Sparkle size={8} weight="fill" /> ai</span>}
                      </p>
                      {it.notes && <p className="text-[10px] text-neutral-500 mt-0.5">{it.notes}</p>}
                    </div>
                    <button onClick={() => handleDelete(it.item_id)}
                      data-testid={`packing-delete-${it.item_id}`}
                      className="opacity-0 group-hover:opacity-100 p-1 rounded text-neutral-500 hover:bg-red-500/10 hover:text-red-400">
                      <Trash size={11} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <PackingEditModal api={api} trip={trip}
          onClose={() => setShowForm(false)}
          onSaved={async () => { setShowForm(false); await fetchItems(); }} />
      )}

      {showAi && (
        <AIPackModal api={api} trip={trip}
          onClose={() => setShowAi(false)}
          onAdded={async () => { setShowAi(false); await fetchItems(); }} />
      )}
    </div>
  );
}

function AIPackModal({ api, trip, onClose, onAdded }) {
  const [extraNotes, setExtraNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');
  const [season, setSeason] = useState('');
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState({}); // label -> bool

  const generate = async () => {
    setError(''); setBusy(true); setItems([]); setSeason('');
    try {
      const r = await api.post(`/trips/${trip.trip_id}/packing/ai-suggest`, {
        extra_notes: extraNotes,
        add_to_list: false,
      });
      setSeason(r.data?.season_inference || '');
      const list = r.data?.items || [];
      setItems(list);
      const initSel = {};
      list.forEach(it => { initSel[it.label] = true; });
      setSelected(initSel);
      if (list.length === 0) setError('AI returned no items. Try again with more notes.');
    } catch (e) {
      setError(e.response?.data?.detail || 'AI suggestion failed');
    }
    setBusy(false);
  };

  const addSelected = async () => {
    const picked = items.filter(it => selected[it.label]);
    if (picked.length === 0) { setError('Pick at least one item to add'); return; }
    setAdding(true);
    try {
      await api.post(`/trips/${trip.trip_id}/packing/bulk`, {
        items: picked.map(p => ({
          label: p.label,
          category: p.category,
          qty: p.qty,
          packed: false,
          notes: p.notes || '',
        })),
      });
      onAdded && onAdded();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to add items');
      setAdding(false);
    }
  };

  const toggleAll = (v) => {
    const next = {};
    items.forEach(it => { next[it.label] = v; });
    setSelected(next);
  };

  const grouped = {};
  items.forEach(it => {
    const k = it.category || 'general';
    if (!grouped[k]) grouped[k] = [];
    grouped[k].push(it);
  });
  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} data-testid="ai-pack-modal"
        className="bg-neutral-900 border border-white/10 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 z-10 px-5 py-3 border-b border-white/10 bg-neutral-900 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkle size={16} weight="fill" className="text-purple-400" />
            <h3 className="text-sm font-bold text-white">AI Packing Assistant</h3>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-white"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-3">
          {error && <div className="text-xs text-red-300 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30" data-testid="ai-pack-error">{error}</div>}

          <div className="rounded-xl p-3" style={{ background: 'rgba(168,85,247,0.06)', border: '1px solid rgba(168,85,247,0.20)' }}>
            <p className="text-[11px] text-neutral-400 mb-1">For:</p>
            <p className="text-sm font-bold text-white">{trip.destination || '—'} · {trip.start_date || 'no date'} · {trip.party_size || 1} traveller(s)</p>
            <p className="text-[10px] text-neutral-500 mt-1">AI infers season/climate from your destination &amp; dates — no weather API needed.</p>
          </div>

          {items.length === 0 && (
            <>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 block mb-1">Extra notes (optional)</label>
                <textarea rows="2" value={extraNotes} onChange={e => setExtraNotes(e.target.value)}
                  data-testid="ai-pack-notes" className="t-input"
                  placeholder="trekking gear, formal evening, baby on board, beach time…" />
              </div>
              <button onClick={generate} disabled={busy} data-testid="ai-pack-generate-btn"
                className="w-full px-4 py-2.5 rounded-xl text-sm font-bold disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #A855F7, #EC4899)', color: 'white' }}>
                {busy ? 'Thinking…' : '✨ Generate packing list'}
              </button>
            </>
          )}

          {items.length > 0 && (
            <>
              {season && (
                <div className="rounded-xl p-3 flex items-start gap-2" style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)' }} data-testid="ai-pack-season">
                  <Sun size={14} weight="fill" className="text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-[12px] text-amber-200 italic">{season}</p>
                </div>
              )}

              <div className="flex items-center justify-between text-[11px]">
                <p className="text-neutral-400">{selectedCount} of {items.length} selected</p>
                <div className="flex gap-1.5">
                  <button onClick={() => toggleAll(true)} data-testid="ai-pack-select-all"
                    className="px-2 py-0.5 rounded bg-white/5 text-neutral-300 hover:bg-white/10 font-semibold">All</button>
                  <button onClick={() => toggleAll(false)} data-testid="ai-pack-select-none"
                    className="px-2 py-0.5 rounded bg-white/5 text-neutral-300 hover:bg-white/10 font-semibold">None</button>
                </div>
              </div>

              <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-1">
                {PACKING_CATEGORIES.filter(c => grouped[c.id]).map(c => (
                  <div key={c.id} className="rounded-xl overflow-hidden"
                    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <div className="px-3 py-1.5 flex items-center gap-2" style={{ background: 'rgba(255,255,255,0.04)' }}>
                      <span className="text-sm">{c.emoji}</span>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-400">{c.label}</span>
                    </div>
                    <div className="divide-y divide-white/5">
                      {grouped[c.id].map(it => (
                        <label key={it.label} className="px-3 py-2 flex items-start gap-2.5 cursor-pointer hover:bg-white/[0.02]"
                          data-testid={`ai-pack-row-${it.label.replace(/\s+/g,'-').toLowerCase()}`}>
                          <input type="checkbox" checked={!!selected[it.label]}
                            onChange={e => setSelected(s => ({ ...s, [it.label]: e.target.checked }))}
                            className="mt-1 accent-[#A855F7]" />
                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] text-white">{it.label}{it.qty > 1 && <span className="text-[10px] text-neutral-500 ml-1">×{it.qty}</span>}</p>
                            {it.notes && <p className="text-[10px] text-neutral-500 mt-0.5">{it.notes}</p>}
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="sticky bottom-0 px-5 py-3 border-t border-white/10 bg-neutral-900 flex justify-end gap-2">
          <button onClick={onClose} disabled={adding || busy} className="px-3 py-2 rounded-lg bg-white/5 text-neutral-300 text-xs font-semibold hover:bg-white/10">Close</button>
          {items.length > 0 && (
            <>
              <button onClick={() => { setItems([]); setSeason(''); }} disabled={adding}
                data-testid="ai-pack-redo-btn"
                className="px-3 py-2 rounded-lg bg-white/5 text-neutral-300 text-xs font-semibold hover:bg-white/10">
                Redo
              </button>
              <button onClick={addSelected} disabled={adding || selectedCount === 0}
                data-testid="ai-pack-add-btn"
                className="px-4 py-2 rounded-lg text-white text-xs font-bold disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #A855F7, #EC4899)' }}>
                {adding ? 'Adding…' : `Add ${selectedCount} to list`}
              </button>
            </>
          )}
        </div>
        <style>{`.t-input{width:100%;padding:8px 12px;border-radius:10px;background:rgba(255,255,255,0.05);border:1px solid rgba(255,255,255,0.10);font-size:13px;color:#fff;outline:none}.t-input:focus{border-color:#A855F7}`}</style>
      </div>
    </div>
  );
}

function PackingEditModal({ api, trip, onClose, onSaved }) {
  const [form, setForm] = useState({ label: '', category: 'general', qty: 1, notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    setError('');
    if (!form.label.trim()) { setError('Item required'); return; }
    setSaving(true);
    try {
      await api.post(`/trips/${trip.trip_id}/packing`, {
        ...form,
        qty: Number(form.qty) || 1,
      });
      onSaved && onSaved();
    } catch (e) { setError(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} data-testid="packing-edit-modal"
        className="bg-neutral-900 border border-white/10 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 z-10 px-5 py-3 border-b border-white/10 bg-neutral-900 flex items-center justify-between">
          <h3 className="text-sm font-bold text-white">Add Packing Item</h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          {error && <div className="text-xs text-red-300 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30">{error}</div>}
          <div>
            <L>Item</L>
            <input value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
              data-testid="pack-label" className="t-input" placeholder="Sunscreen, Swimsuit, Adapter…" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <L>Quantity</L>
              <input type="number" min="1" max="50" value={form.qty}
                onChange={e => setForm(f => ({ ...f, qty: e.target.value }))}
                data-testid="pack-qty" className="t-input" />
            </div>
            <div>
              <L>Category</L>
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                data-testid="pack-category" className="t-input">
                {PACKING_CATEGORIES.map(c => (
                  <option key={c.id} value={c.id} style={{ background: '#0a0a0a' }}>{c.emoji} {c.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <L>Notes</L>
            <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              data-testid="pack-notes" className="t-input" placeholder="optional" />
          </div>
        </div>
        <div className="sticky bottom-0 px-5 py-3 border-t border-white/10 bg-neutral-900 flex justify-end gap-2">
          <button onClick={onClose} disabled={saving} className="px-3 py-2 rounded-lg bg-white/5 text-neutral-300 text-xs font-semibold hover:bg-white/10">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="pack-save-btn"
            className="px-4 py-2 rounded-lg bg-[#FF6B35] text-white text-xs font-bold disabled:opacity-50">
            {saving ? 'Saving…' : 'Add'}
          </button>
        </div>
        <style>{`.t-input{width:100%;padding:8px 12px;border-radius:10px;background:rgba(255,255,255,0.05);border:1px solid rgba(255,255,255,0.10);font-size:13px;color:#fff;outline:none}.t-input:focus{border-color:#FF6B35}`}</style>
      </div>
    </div>
  );
}

function L({ children }) {
  return <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 block mb-1">{children}</label>;
}
